"use client";

import { Component, PropsWithChildren } from "react";
import Button from "components/base/button/Button";
import styles from "./error-boundary.module.sass";

interface IErrorBoundaryProps {
    name?: string;
    fallback?: React.ReactNode;
}

interface IErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

class ErrorBoundary extends Component<PropsWithChildren<IErrorBoundaryProps>, IErrorBoundaryState> {
    constructor(props: IErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: undefined };
    }

    static getDerivedStateFromError(error: Error): IErrorBoundaryState {
        // Let Next.js notFound() errors bubble up to not-found.tsx
        if (ErrorBoundary.isNotFoundError(error)) {
            throw error;
        }

        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        // Re-throw notFound() errors to prevent them from being caught
        if (ErrorBoundary.isNotFoundError(error)) {
            throw error;
        }

        // Log error details for debugging
        const boundaryName = this.props.name || "Unknown";
        console.error(`[ErrorBoundary: ${boundaryName}]`, {
            error,
            errorInfo,
            componentStack: errorInfo.componentStack,
        });
    }

    private static isNotFoundError(error: Error): boolean {
        const errorMessage = error.message || "";
        const errorDigest = (error as any).digest || "";

        return errorMessage.includes("404") || errorDigest.includes("404");
    }

    private handleReset = (): void => {
        this.setState({ hasError: false, error: undefined });
    };

    render(): React.ReactNode {
        const { hasError } = this.state;
        const { children, fallback } = this.props;

        if (hasError) {
            // Use custom fallback if provided, otherwise use default
            if (fallback) {
                return fallback;
            }

            return (
                <div className={styles.container}>
                    <h1 className={styles.text}>Something went wrong.</h1>
                    <Button
                        type="button"
                        variant="primary"
                        className={styles.button}
                        onClick={this.handleReset}>
                        Try again
                    </Button>
                </div>
            );
        }

        return children;
    }
}

export default ErrorBoundary;
